import { BotWalletSupportedChain } from "@/providers/wallet";
import { api, type Api } from "@api/bot-base-api-request";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

const BOT_GET_TOKEN_INFO_QUERY_KEY = "bot-get-token-info";

type BotGetTokenInfoParams = {
  appPubKey: string;
  userId: string;
  chainId: number;
  tokenAddress: string;
};

type Token = {
  name: string;
  symbol: string;
  decimals?: number;
  address: string;
};

type PairInfo = {
  address: string;
  baseToken: Token;
  quoteToken: Token;
};

export type Pair = PairInfo & {
  priceUsd: number;
  priceInNative: number;
  nativePrice: number;
};

type BotTokenInfoResponse = {
  pair: Pair;
  tokenUserBalances?: string[];
};

const fetchBotTokenInfo = async (
  api: Api,
  params: BotGetTokenInfoParams,
): Promise<any> => api.get("tokenInfo", { searchParams: params }).json();

const useGetBotTokenInfo = (
  params: BotGetTokenInfoParams,
  enabled: boolean = true,
) => {
  const transformResponse = (data: any) => {
    const tokenData = data.tokenData;
    const isSolanaPair = params.chainId === BotWalletSupportedChain.SOL;
    const response: BotTokenInfoResponse = {
      pair: {
        address: tokenData.pair.address,
        baseToken: tokenData.pair.baseToken,
        quoteToken: tokenData.pair.quoteToken,
        priceUsd: Number(tokenData.pair.priceUsd),
        priceInNative: isSolanaPair
          ? Number(tokenData.pair.priceNative)
          : tokenData.pair.priceInNative,
        nativePrice: isSolanaPair
          ? (1 / Number(tokenData.pair.priceNative)) *
            Number(tokenData.pair.priceUsd)
          : tokenData.pair.nativePrice,
      },
      tokenUserBalances: isSolanaPair
        ? tokenData.tokenBalances
        : tokenData.tokenUserBalances,
    };
    return response;
  };
  const selector = useCallback(transformResponse, []);

  return useQuery<BotTokenInfoResponse>({
    queryKey: [BOT_GET_TOKEN_INFO_QUERY_KEY, params],
    queryFn: async () => fetchBotTokenInfo(api, params),
    enabled: !!params.tokenAddress && enabled,
    select: selector,
  });
};

export { useGetBotTokenInfo, fetchBotTokenInfo, BOT_GET_TOKEN_INFO_QUERY_KEY };
